"use client";

import { useTranslations } from "next-intl";

import type {
  ReplayMetricKey,
  ReplayMetricValue,
  ReplayPlayerMetrics,
} from "@/entities/replay-metrics";
import { MetricsPlayerLabel, useMatchReplayMetrics } from "@/entities/replay-metrics";

/**
 * KAN-727: профиль команд в разборе матча (эпик KAN-719).
 *
 * Таблица цифр требует, чтобы читатель сам сделал выводы. Здесь выводы уже сделаны: по
 * каждой поведенческой метрике показан игрок, у которого её больше всех в команде. Это
 * отвечает на вопросы, на которые KDA не отвечает — кто вёл команду, кто играл на
 * объекты, кто держал контроль.
 *
 * ПЕРЦЕНТИЛЕЙ ЗДЕСЬ НЕТ И БЫТЬ НЕ МОЖЕТ. Группа сравнения — турнир, а не матч: сравнивать
 * игрока с четырьмя одноклубниками бессмысленно. Поэтому показываются абсолютные значения,
 * и подпись говорит именно «больше всех в команде», а не «выше N%».
 *
 * Метрики без данных не показываются вовсе. Строка «лидер по дайвам: никто, 0» была бы
 * неправдой, когда поля миссий в патче просто нет.
 */

interface MatchBehaviorProfileProps {
  matchId: string;
}

/** Метрики, у которых «больше» — осмысленное утверждение о стиле команды. */
const HIGHLIGHT_KEYS: ReplayMetricKey[] = [
  "shotcalling",
  "objectiveFocus",
  "towerDiveAggression",
  "ccPressure",
  "visionQuality",
];

function formatValue(key: ReplayMetricKey, value: number): string {
  if (key === "visionQuality" || key === "goldUtilization") {
    return `${Math.round(value * 100)}%`;
  }
  if (key === "objectiveFocus") return value.toFixed(2);
  return value >= 10 ? Math.round(value).toString() : value.toFixed(1);
}

/**
 * Лидер команды по метрике. null — ни у кого нет данных по ней: в этом патче поля не
 * было, и «лидер» назвать некого.
 */
function leaderOf(
  players: ReplayPlayerMetrics[],
  key: ReplayMetricKey,
): { player: ReplayPlayerMetrics; metric: ReplayMetricValue } | null {
  let best: { player: ReplayPlayerMetrics; metric: ReplayMetricValue } | null = null;

  for (const player of players) {
    const metric = player.metrics.find((m) => m.key === key);
    if (!metric || metric.value === null) continue;
    if (best === null || metric.value > best.metric.value!) {
      best = { player, metric };
    }
  }

  return best;
}

export function MatchBehaviorProfile({ matchId }: MatchBehaviorProfileProps) {
  const t = useTranslations("Profile.behaviorMetrics");
  const tm = useTranslations("Bracket.modals.matchStats.behavior");
  const { data, isLoading, isError } = useMatchReplayMetrics(matchId);

  if (isLoading || isError || !data || data.length === 0) return null;

  // Стороны из реплея: "100" — синие, "200" — красные. Игроки без стороны (реплей без
  // поля TEAM) в командный профиль не идут: приписать их к чужой команде хуже, чем не
  // показать вовсе.
  const blue = data.filter((p) => p.teamSide === "100");
  const red = data.filter((p) => p.teamSide === "200");
  if (blue.length === 0 && red.length === 0) return null;

  return (
    <div className="rounded-lg border bg-muted/20 p-3 space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {tm("title")}
      </div>
      <p className="text-xs text-muted-foreground">{tm("subtitle")}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        <TeamColumn players={blue} side="blue" t={t} tm={tm} />
        <TeamColumn players={red} side="red" t={t} tm={tm} />
      </div>
    </div>
  );
}

function TeamColumn({
  players,
  side,
  t,
  tm,
}: {
  players: ReplayPlayerMetrics[];
  side: "blue" | "red";
  t: ReturnType<typeof useTranslations>;
  tm: ReturnType<typeof useTranslations>;
}) {
  const leaders = HIGHLIGHT_KEYS.map((key) => ({ key, best: leaderOf(players, key) })).filter(
    (row): row is { key: ReplayMetricKey; best: NonNullable<ReturnType<typeof leaderOf>> } =>
      row.best !== null,
  );

  return (
    <div className="space-y-2">
      <div
        className={`text-sm font-semibold ${
          side === "blue" ? "text-blue-600" : "text-red-600"
        }`}
      >
        {tm(side)}
      </div>

      {leaders.length === 0 ? (
        <p className="text-xs text-muted-foreground">{tm("noData")}</p>
      ) : (
        <ul className="space-y-1">
          {leaders.map(({ key, best }) => (
            <li key={key} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="text-muted-foreground truncate">{t(`axis.${key}`)}</span>
              <span className="flex items-baseline gap-2 shrink-0">
                {/* KAN-743: имя профиля со ссылкой; Riot-ник — только у тех, кого нет на сайте. */}
                <span className="truncate max-w-[9rem]">
                  <MetricsPlayerLabel player={best.player} showAvatar={false} />
                </span>
                <span className="font-semibold tabular-nums">
                  {formatValue(key, best.metric.value!)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
